import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { createPageUrl } from "../utils";
import { useAccommodations } from "../contexts/AccommodationContext";
import { useImages } from "@agape/frontend-utils";
import { Users, Calendar, ArrowRight, Maximize2 } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { useLanguage } from "../contexts/LanguageProvider";
import { CONTACT_INFO, ACCESSIBILITY_OVERLAYS_ENABLED, BOOKINGS_APP_URL } from "../config/constants";
import anoViannosBackground from "../assets/ano-viannos-background.jpg";
import { AmenityList } from "@agape/frontend-utils";
import { 
  getAccommodationDisplayName, 
  sortAccommodationsAlphabetically 
} from "../utils/accommodationUtils";

export default function Home() {
  const [featuredAccommodations, setFeaturedAccommodations] = useState([]);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();
  const { accommodations, loading: loadingAccommodations } = useAccommodations();
  const { images: allImages } = useImages();

  // Translations for Home page
  const homeTranslations = {
    en: {
      welcomeTo: "Welcome to",
      crete: `${CONTACT_INFO.GUESTHOUSE_NAME}`,
      heroDescription: "Experience authentic Cretan hospitality in our charming guesthouse, where Mediterranean dreams come to life",
      exploreAccommodations: "Explore Our Accommodations",
      bookStay: "Book Your Stay",
      featuredAccommodations: "Our Featured Accommodations",
      guests: "guests",
      viewDetails: "View Details",
      viewAllAccommodations: "View All Accommodations",
      whatGuestsSay: "What Our Guests Say",
      whatGuestsSayDesc: "Real experiences from our valued guests",
      readAllReviews: "Read All Reviews",
      readyForAdventure: "Ready for Your Cretan Adventure?",
      readyDescription: "Book your stay today and experience the warmth of authentic Greek hospitality",
      bookNow: "Book Now",
      perNight: "/night"
    },
    gr: {
      welcomeTo: "Καλώς ήρθατε στο",
      crete: `${CONTACT_INFO.GUESTHOUSE_NAME}`,
      heroDescription: "Ζήστε την αυθεντική κρητική φιλοξενία στον γοητευτικό ξενώνα μας, όπου τα μεσογειακά όνειρα παίρνουν ζωή",
      exploreAccommodations: "Εξερευνήστε τα Καταλύματά μας",
      bookStay: "Κλείστε τη Διαμονή σας",
      featuredAccommodations: "Τα Προτεινόμενα Καταλύματά μας",
      guests: "επισκέπτες",
      viewDetails: "Δείτε Λεπτομέρειες",
      viewAllAccommodations: "Δείτε Όλα τα Καταλύματα",
      whatGuestsSay: "Τι Λένε οι Επισκέπτες μας",
      whatGuestsSayDesc: "Πραγματικές εμπειρίες από τους εκτιμημένους επισκέπτες μας",
      readAllReviews: "Διαβάστε Όλες τις Κριτικές",
      readyForAdventure: "Έτοιμοι για την Κρητική σας Περιπέτεια;",
      readyDescription: "Κλείστε τη διαμονή σας σήμερα και ζήστε τη ζεστασιά της αυθεντικής ελληνικής φιλοξενίας",
      bookNow: "Κλείστε Τώρα",
      perNight: "/βράδυ"
    }
  };

  const ht = (key) => homeTranslations[language][key] || key;

  useEffect(() => {
    if (accommodations.length > 0) {
      const updatedAccommodations = accommodations.map(accommodation => {
        if (!accommodation.main_image && accommodation.photos_storage_path && allImages.length > 0) {
          const defaultImage = allImages.find(image => image.category === accommodation.photos_storage_path);
          return { ...accommodation, main_image: defaultImage ? defaultImage.url : null };
        }
        return accommodation;
      });
      const sortedAccommodations = sortAccommodationsAlphabetically(updatedAccommodations);
      setFeaturedAccommodations(sortedAccommodations.slice(0, 3));
    }
  }, [accommodations, allImages]);

  useEffect(() => {
    setLoading(loadingAccommodations);
  }, [loadingAccommodations]);

  return (
    <div>
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={anoViannosBackground}
            alt="Ano Viannos village"
            className="w-full h-full object-cover"
          />
          <div className={`absolute inset-0 ${ACCESSIBILITY_OVERLAYS_ENABLED ? 'bg-gradient-to-r from-blue-900/70 to-blue-800/50' : 'bg-gradient-to-r from-gray-900/35 to-gray-800/25'}`}></div>
        </div>

        <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-4">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight px-6 py-2 rounded-lg inline-block bg-black/60">
            {ht('welcomeTo')}
            <span className="block text-white">
              {ht('crete')}
            </span>
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-white max-w-2xl mx-auto leading-relaxed px-6 py-3 rounded-lg font-semibold bg-black/50">
            {ht('heroDescription')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={createPageUrl("Accommodations")}>
              <Button size="lg" className="bg-white text-blue-900 hover:bg-blue-50 font-semibold px-8 py-4 text-lg">
                {ht('exploreAccommodations')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href={`${BOOKINGS_APP_URL}/stay`} target="_blank" rel="noopener noreferrer">
              <Button
                size="lg"
                className="bg-blue-600 text-white hover:bg-blue-700 font-semibold px-8 py-4 text-lg"
              >
                {ht('bookStay')}
              </Button>
            </a>
          </div>
        </div>
      </section>


      {/* Featured Accommodations */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{ht('featuredAccommodations')}</h2>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-2xl shadow-lg animate-pulse">
                  <div className="h-64 bg-gray-200 rounded-t-2xl"></div>
                  <div className="p-6 space-y-4">
                    <div className="h-6 bg-gray-200 rounded"></div>
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {featuredAccommodations.map((accommodation) => (
                <Card key={accommodation.id} className="group overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-500 h-full flex flex-col">
                  <div className="relative overflow-hidden">
                    {accommodation.main_image ? (
                      <img
                        src={accommodation.main_image}
                        alt={getAccommodationDisplayName(accommodation)}
                        className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-64 bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center">
                        <span className="text-blue-400 text-lg font-medium">{getAccommodationDisplayName(accommodation)}</span>
                      </div>
                    )}
                    <div className="absolute top-4 right-4 bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      €{accommodation.price_per_night}{ht('perNight')}
                    </div>
                  </div>
                  <CardContent className="p-6 flex-1 flex flex-col">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{getAccommodationDisplayName(accommodation)}</h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">{accommodation.description}</p>
                    
                    {/* Accommodation Details */}
                    <div className="flex flex-wrap gap-4 mb-4 text-gray-700">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium">{accommodation.capacity}</span>
                      </div>
                      {accommodation.size && (
                        <div className="flex items-center gap-2">
                          <Maximize2 className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-medium">{accommodation.size} m²</span>
                        </div>
                      )}
                    </div>

                    {/* Amenities */}
                    <div className="mb-6 flex-grow">
                      <AmenityList
                        amenityKeys={accommodation.amenities || []}
                        compact={true}
                        showIcons={true}
                      />
                    </div>

                    {/* Button always at bottom */}
                    <div className="mt-auto">
                      <Link to={`${createPageUrl("Accommodations")}#accommodation-${accommodation.id}`}>
                        <Button className="w-full bg-blue-600 hover:bg-blue-700">
                          {ht('viewDetails')}
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <div className="text-center mt-12">
            <Link to={createPageUrl("Accommodations")}>
              <Button size="lg" variant="outline" className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white">
                {ht('viewAllAccommodations')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Calendar className="w-16 h-16 mx-auto mb-6 text-blue-200" />
          <h2 className="text-4xl font-bold mb-6">{ht('readyForAdventure')}</h2>
          <p className="text-xl mb-8 text-blue-100">
            {ht('readyDescription')}
          </p>
          <a href={`${BOOKINGS_APP_URL}/stay`} target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-blue-50 font-semibold px-8 py-4 text-lg">
              {ht('bookNow')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
}
